import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import BookingModal from './BookingModal';
import BookingDetailsModal from './BookingDetailsModal';
import './CalendarGrid.css';

export default function CalendarGrid({ calendarId }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [isLargeView, setIsLargeView] = useState(() => (localStorage.getItem('calendarCellSize') || 'normal') === 'large');
  const { currentUser } = useAuth();

  // Apply body class so CalendarView.css parent containers can be scoped too
  useEffect(() => {
    if (isLargeView) {
      document.body.classList.add('cell-size-large');
    } else {
      document.body.classList.remove('cell-size-large');
    }
    return () => document.body.classList.remove('cell-size-large');
  }, [isLargeView]);

  // Listen for preference changes from the settings modal
  useEffect(() => {
    function handleCellSizeChange(e) {
      setIsLargeView(e.detail === 'large');
    }
    window.addEventListener('calendarCellSizeChange', handleCellSizeChange);
    return () => window.removeEventListener('calendarCellSizeChange', handleCellSizeChange);
  }, []);

  // Arabic day names
  const arabicDays = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  // Arabic month names
  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  // Get current month and year
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Get number of days in current month
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Navigate to previous month
  function goToPreviousMonth() {
    setCurrentDate(new Date(year, month - 1, 1));
  }

  // Navigate to next month
  function goToNextMonth() {
    setCurrentDate(new Date(year, month + 1, 1));
  }

  // Check if viewing current month
  const today = new Date();
  const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();

  // Go to today
  function goToToday() {
    setCurrentDate(new Date());
  }

  // Toggle date picker
  function toggleDatePicker() {
    if (!showDatePicker) {
      setPickerYear(year);
    }
    setShowDatePicker(!showDatePicker);
  }

  // Select month from picker
  function handleMonthSelect(selectedMonth) {
    setCurrentDate(new Date(pickerYear, selectedMonth, 1));
    setShowDatePicker(false);
  }

  // Generate calendar days array
  function generateCalendarDays() {
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  const calendarDays = generateCalendarDays();

  // Fetch bookings for this calendar in real-time
  useEffect(() => {
    const bookingsRef = collection(db, 'bookings');
    const q = query(bookingsRef, where('calendarId', '==', calendarId));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const bookingsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBookings(bookingsList);
        setBookingsLoaded(true);
      },
      (error) => {
        console.error('Error fetching bookings:', error);
      }
    );

    return () => unsubscribe();
  }, [calendarId]);

  // Check if a date is booked
  function getBookingForDate(day) {
    // Create date string in YYYY-MM-DD format
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    return bookings.find(booking => {
      // Handle both old ISO format and new YYYY-MM-DD format
      let bookingDateStr = booking.date;
      if (booking.date && booking.date.includes('T')) {
        // Old ISO format - extract just the date part
        bookingDateStr = booking.date.split('T')[0];
      }
      return bookingDateStr === dateStr;
    });
  }

  // Fetch calendar data in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'calendars', calendarId),
      (calendarDoc) => {
        if (calendarDoc.exists()) {
          setCalendar({ id: calendarDoc.id, ...calendarDoc.data() });
        }
      },
      (error) => {
        console.error('Error fetching calendar:', error);
      }
    );

    return () => unsubscribe();
  }, [calendarId]);

  // Check if booking belongs to current user
  function isMyBooking(booking) {
    if (!currentUser || !booking) return false;
    return booking.bookedBy === currentUser.uid;
  }

  // Check if a day is today
  function isToday(day) {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  }

  // Check if a day is in the past
  function isPastDay(day) {
    if (!day) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  }

  // Handle date click
  function handleDateClick(day) {
    if (!day || !bookingsLoaded) return; // Ignore empty cells or clicks before bookings are loaded

    const booking = getBookingForDate(day);

    if (booking) {
      // Show booking details if date is already booked
      setSelectedBooking(booking);
      setShowDetailsModal(true);
    } else if (!isPastDay(day)) {
      // Show booking modal only if date is available and not in the past
      // Store date as YYYY-MM-DD string to avoid timezone issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedDate(dateStr);
      setShowBookingModal(true);
    }
  }

  // Handle booking success
  function handleBookingSuccess() {
    setShowBookingModal(false);
    setSelectedDate(null);
    // Bookings will update automatically via real-time listener
  }

  // Handle booking deleted
  function handleBookingDeleted() {
    setShowDetailsModal(false);
    setSelectedBooking(null);
    // Bookings will update automatically via real-time listener
  }

  return (
    <div className={`calendar-grid-container ${isLargeView ? 'calendar-large' : 'calendar-normal'}`}>
      {/* Month Navigation Header */}
      <div className="calendar-header">
        <button className="nav-btn" onClick={goToPreviousMonth}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        <div className="calendar-month-year-wrapper">
          <h2 className="calendar-month-year" onClick={toggleDatePicker}>
            {arabicMonths[month]} {year}
            <svg className={`picker-arrow ${showDatePicker ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </h2>

          {showDatePicker && (
            <>
              <div className="date-picker-overlay" onClick={() => setShowDatePicker(false)} />
              <div className="date-picker-dropdown">
                <div className="picker-year-selector">
                  <button className="picker-year-btn" onClick={() => setPickerYear(pickerYear - 1)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                  <span className="picker-year-text">{pickerYear}</span>
                  <button className="picker-year-btn" onClick={() => setPickerYear(pickerYear + 1)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>
                </div>
                <div className="picker-month-grid">
                  {arabicMonths.map((monthName, i) => (
                    <button
                      key={i}
                      className={`picker-month-btn ${i === month && pickerYear === year ? 'active' : ''} ${i === today.getMonth() && pickerYear === today.getFullYear() ? 'current' : ''}`}
                      onClick={() => handleMonthSelect(i)}
                    >
                      {monthName}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <button className="nav-btn" onClick={goToNextMonth}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      </div>

      {/* Go to Today Button */}
      {!isCurrentMonth && (
        <button className="go-to-today-btn" onClick={goToToday}>
          اليوم
        </button>
      )}

      {/* Day Names Header */}
      <div className="calendar-days-header">
        {arabicDays.map((day, index) => (
          <div key={index} className="day-name">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {calendarDays.map((day, index) => {
          const booking = day ? getBookingForDate(day) : null;
          const isBooked = !!booking;
          const isMine = isMyBooking(booking);
          const isTodayDate = isToday(day);
          const isPast = isPastDay(day);

          return (
            <div
              key={index}
              className={`calendar-cell ${day ? 'has-date' : 'empty'} ${isBooked ? 'booked' : ''} ${isMine ? 'my-booking' : ''} ${isTodayDate ? 'today' : ''} ${isPast ? 'past' : ''}`}
              onClick={() => handleDateClick(day)}
            >
              {day && (
                <div className="date-content">
                  <span className="date-number">{day}</span>
                  {isBooked && (
                    <div className="booking-indicator">
                      <span className="booking-name">{booking.guestName}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-box available"></span>
          <span className="legend-label">متاح</span>
        </div>
        <div className="legend-item">
          <span className="legend-box booked"></span>
          <span className="legend-label">محجوز</span>
        </div>
        {currentUser && (
          <div className="legend-item">
            <span className="legend-box my-booking"></span>
            <span className="legend-label">حجزي</span>
          </div>
        )}
        <div className="legend-item">
          <span className="legend-box past"></span>
          <span className="legend-label">منتهي</span>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedDate && (
        <BookingModal
          date={selectedDate}
          calendarId={calendarId}
          calendar={calendar}
          onClose={() => setShowBookingModal(false)}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Booking Details Modal */}
      {showDetailsModal && selectedBooking && calendar && (
        <BookingDetailsModal
          booking={selectedBooking}
          calendar={calendar}
          onClose={() => setShowDetailsModal(false)}
          onDeleted={handleBookingDeleted}
        />
      )}
    </div>
  );
}